import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Box, Collapse, FormControl, MenuItem, Select, Skeleton, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BookmarksOutlinedIcon from '@mui/icons-material/BookmarksOutlined';
import { resources } from '../../../core/services';
import { GlassPanel } from '../../../shared/components/GlassPanel';
import { IconTile } from '../../../shared/components/IconTile';
import { LiveDot } from '../../../shared/components/LiveDot';
import { Pill } from '../../../shared/components/Pill';
import { SectionCollapseButton, useSectionCollapse } from '../../../shared/components/SectionCollapseButton';
import { formatEntityTypeLabel } from '../../../shared/components/entityTypeLabel';
import { usePermissions } from '../../../shared/hooks/usePermissions';
import type { Entity, EntityTypeSkill, Job, EntityTypeEnrichment } from '../../../core/types/data-models';
import { ManagedTypeCard } from './ManagedTypeCard';
import { RecentAnalysesStrip } from './RecentAnalysesStrip';

// ── Project Key Entities & Insights (project dashboard block) ────────────────
//
// One ManagedTypeCard per ProjectManagedEntityType row (the sidebar's MANAGED
// KNOWLEDGE list), a RecentAnalysesStrip of the newest insight topics above
// the cards, and an "Add managed entity type" picker below them. With
// `allEntityTypes` (the validation summary) the cards are every entity type
// extracted for the project instead, and there is no picker.
//
// Query keys are shared with ManagedKnowledgeList (sidebar) so adding or
// removing a managed type in either place refreshes both.

const ENRICHMENT_JOB_TYPE = 'GENERATE_ENTITY_TYPE_ENRICHMENT';
/** Most recently generated insight topics shown in the "Recent Analyses" strip. */
const RECENT_ANALYSES_LIMIT = 3;

/** Identity of one (entityType, skillId) run — the unit the enrichment job replaces. */
function runKey(entityType: string, skillId: number): string {
  return `${entityType}::${skillId}`;
}

interface ManagedKnowledgeOverviewSectionProps {
  projectId: number;
  /** The project's PENDING/PROCESSING jobs — ProjectDetail already polls these every few seconds. */
  activeJobs: Job[];
  onOpenEntity: (entity: Entity) => void;
  /** Height cap for the dashboard row this block shares; the body scrolls inside it. */
  maxHeight?: number;
  /** A card for every entity type extracted for the project rather than for the managed ones. */
  allEntityTypes?: boolean;
}

export function ManagedKnowledgeOverviewSection({
  projectId,
  activeJobs,
  onOpenEntity,
  maxHeight,
  allEntityTypes = false,
}: ManagedKnowledgeOverviewSectionProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canEdit } = usePermissions();

  const managedApi = resources.projectManagedEntityTypesFor(projectId);
  const managedQuery = useQuery({
    queryKey: ['project-managed-entity-types', projectId],
    queryFn: () => managedApi.list({ pageSize: 100, sort: 'order,id' }),
    enabled: Number.isFinite(projectId) && !allEntityTypes,
  });
  const managedTypes = managedQuery.data?.data ?? [];

  // Every type extracted for the project so far (same source as the
  // sidebar's control): the cards with `allEntityTypes`, else the "Add
  // entity type" pool.
  const entityTypesQuery = useQuery({
    queryKey: ['project-entity-types', projectId],
    queryFn: () => resources.getProjectEntityTypes(projectId),
    enabled: Number.isFinite(projectId) && (canEdit || allEntityTypes),
  });
  const shownTypes = allEntityTypes ? (entityTypesQuery.data ?? []) : managedTypes.map((t) => t.entityType);

  const entitiesQuery = useQuery({
    queryKey: ['managed-knowledge-entity-counts', projectId, shownTypes],
    queryFn: () =>
      resources.entities.list({
        filter: { projectId: [projectId], entityType: shownTypes },
        pageSize: 2000,
      }),
    enabled: Number.isFinite(projectId) && shownTypes.length > 0,
  });

  const enrichmentsQuery = useQuery({
    queryKey: ['project-managed-enrichments', projectId],
    queryFn: () =>
      resources.entityTypeEnrichments.list({
        filter: { projectId: [projectId] },
        sort: 'order',
        pageSize: 200,
      }),
    enabled: Number.isFinite(projectId) && shownTypes.length > 0,
  });

  // The skill catalog is global (not per project), so one request covers
  // every managed type; grouped by entityType below.
  const skillsQuery = useQuery({
    queryKey: ['entity-type-skills', 'ACTIVE'],
    queryFn: () => resources.entityTypeSkills.list({ filter: { status: 'ACTIVE' }, sort: 'order', pageSize: 1000 }),
    enabled: shownTypes.length > 0,
  });

  const addType = useMutation({
    mutationFn: (entityType: string) => {
      const nextOrder = managedTypes.reduce((max, t) => Math.max(max, t.order), -1) + 1;
      return managedApi.create({ entityType, order: nextOrder });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['project-managed-entity-types', projectId] }),
  });

  const entitiesByType = useMemo(() => {
    const map = new Map<string, Entity[]>();
    for (const e of entitiesQuery.data?.data ?? []) {
      const list = map.get(e.entityType) ?? [];
      list.push(e);
      map.set(e.entityType, list);
    }
    return map;
  }, [entitiesQuery.data]);

  // ManagedTypeCard's "N/M Analyses" stat and its "open on this tab" links
  // only make sense for ENTITY_TYPE-scope topics/skills — an ENTITY-scope
  // skill produces one topic per entity rather than one for the type, and
  // its topics have no `?tab=` of their own (they're shown on the entity's
  // own card in MainTab instead), so both are grouped from the type-level
  // subset only.
  const topicsByType = useMemo(() => {
    const map = new Map<string, EntityTypeEnrichment[]>();
    for (const t of enrichmentsQuery.data?.data ?? []) {
      if (t.entityId != null) continue;
      const list = map.get(t.entityType) ?? [];
      list.push(t);
      map.set(t.entityType, list);
    }
    return map;
  }, [enrichmentsQuery.data]);

  const skills = skillsQuery.data?.data ?? [];
  const skillsByType = useMemo(() => {
    const map = new Map<string, EntityTypeSkill[]>();
    for (const s of skills) {
      if (s.scope === 'ENTITY') continue;
      const list = map.get(s.entityType) ?? [];
      list.push(s);
      map.set(s.entityType, list);
    }
    return map;
  }, [skills]);

  // Which (entityType, skillId) runs are in flight right now, read off the
  // project's active jobs so the state survives reloads and other tabs.
  const runningKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const job of activeJobs) {
      if (job.jobType !== ENRICHMENT_JOB_TYPE || !job.data) continue;
      try {
        const data = JSON.parse(job.data) as { entityType?: string; skillId?: number };
        if (data.entityType && data.skillId != null) keys.add(runKey(data.entityType, data.skillId));
      } catch {
        // Malformed job data — nothing to mark as running.
      }
    }
    return keys;
  }, [activeJobs]);

  // When a run drops out of the active set it has finished (or failed), so
  // the topics list is refreshed to pick up the new/replaced insight.
  const prevRunningKeys = useRef<Set<string>>(new Set());
  useEffect(() => {
    const finished = [...prevRunningKeys.current].some((k) => !runningKeys.has(k));
    if (finished) {
      queryClient.invalidateQueries({ queryKey: ['project-managed-enrichments', projectId] });
      queryClient.invalidateQueries({ queryKey: ['managed-knowledge-enrichments', projectId] });
    }
    prevRunningKeys.current = runningKeys;
  }, [runningKeys, queryClient, projectId]);

  const [expanded, toggleExpanded] = useSectionCollapse('projectKeyEntities');
  const cardsLoading = entitiesQuery.isLoading || enrichmentsQuery.isLoading || skillsQuery.isLoading;

  // The skill runs behind the newest insight topics, whatever type they belong
  // to — the "what did the AI just work on" strip above the cards. Limited to
  // ENTITY_TYPE-scope topics: an ENTITY-scope topic has no `?tab=` of its own
  // to open onto (it's shown on its entity's own card instead), so it would
  // silently bounce back to the Main tab if it were clicked here.
  const recentTopics = useMemo(
    () =>
      (enrichmentsQuery.data?.data ?? [])
        .filter((t) => t.entityId == null)
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
        .slice(0, RECENT_ANALYSES_LIMIT),
    [enrichmentsQuery.data],
  );

  const openType = (entityType: string, tab?: string) => {
    const base = `/projects/${projectId}/managed-knowledge/${encodeURIComponent(entityType)}`;
    navigate(tab ? `${base}?tab=${encodeURIComponent(tab)}` : base);
  };

  if (allEntityTypes ? entityTypesQuery.isPending : managedQuery.isPending) return null;
  if (!allEntityTypes && managedTypes.length === 0 && !canEdit) return null;

  const usedTypes = new Set(shownTypes);
  const availableTypes = allEntityTypes ? [] : (entityTypesQuery.data ?? []).filter((t) => !usedTypes.has(t));

  const totalEntities = entitiesQuery.data?.data?.length ?? 0;
  const totalTopics = enrichmentsQuery.data?.data?.length ?? 0;
  const anyRunning = runningKeys.size > 0;

  return (
    // Stretching to the row makes this and the Market Radar beside it the same
    // height; the cap keeps the row from growing past it and hands the leftover
    // height to the scroll region below.
    <GlassPanel
      sx={{
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        maxHeight: expanded ? maxHeight : undefined,
        alignSelf: expanded ? 'stretch' : 'start',
      }}
    >
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        flexWrap="wrap"
        sx={{ mb: expanded ? 2 : 0, rowGap: 1, flexShrink: 0 }}
      >
        <IconTile icon={<BookmarksOutlinedIcon fontSize="inherit" />} color="primary" size="sm" />
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
            Project Key Entities &amp; Insights
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {allEntityTypes
              ? 'Every entity type found in the project, its entities, AI insight topics, and skills you can run on them'
              : 'Key entity types, their entities, AI insight topics, and skills you can run on them'}
          </Typography>
        </Box>
        {shownTypes.length > 0 && (
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" sx={{ rowGap: 0.5 }}>
            {anyRunning && (
              <Pill color="success" mono={false}>
                <LiveDot size={6} /> Generating…
              </Pill>
            )}
            <Pill>{shownTypes.length} type{shownTypes.length === 1 ? '' : 's'}</Pill>
            <Pill>{totalEntities} entit{totalEntities === 1 ? 'y' : 'ies'}</Pill>
            <Pill>{totalTopics} insight{totalTopics === 1 ? '' : 's'}</Pill>
          </Stack>
        )}
        <SectionCollapseButton
          expanded={expanded}
          onToggle={toggleExpanded}
          label="Project Key Entities & Insights"
        />
      </Stack>

      {/* The scroll region is a direct flex child of the capped panel, so its
          height is bounded without anything being threaded through Collapse. */}
      <Box sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', pr: 0.75 }}>
        <Collapse in={expanded}>
          {shownTypes.length === 0 ? (
            <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic', mb: 1.5 }}>
              {allEntityTypes
                ? 'No entities extracted for this project yet.'
                : 'No managed entity types yet. Pick the entity types that matter most for this project (competitors, ' +
                  'customer segments, risks, …) to track their entities here and run AI research skills on them.'}
            </Typography>
          ) : cardsLoading ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)', xl: 'repeat(4, 1fr)' },
                gap: 1.5,
              }}
            >
              {shownTypes.map((entityType) => (
                <Skeleton key={entityType} variant="rounded" height={86} />
              ))}
            </Box>
          ) : (
            <>
              {/* Newest insight topics across every managed type — stays put
                  while the cards below it scroll. */}
              {recentTopics.length > 0 && (
                <RecentAnalysesStrip
                  topics={recentTopics}
                  onOpen={(topic) => openType(topic.entityType, String(topic.id))}
                />
              )}

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)', xl: 'repeat(4, 1fr)' },
                  gap: 1.5,
                  mb: canEdit && availableTypes.length > 0 ? 1.5 : 0,
                }}
              >
                {shownTypes.map((entityType) => {
                  const typeSkills = skillsByType.get(entityType) ?? [];
                  // Running state covers every scope (an ENTITY-scope run in
                  // flight should still light the LiveDot here), even though
                  // the Analyses stat itself stays type-level only.
                  const allTypeSkills = skills.filter((s) => s.entityType === entityType);
                  return (
                    <ManagedTypeCard
                      key={entityType}
                      entityType={entityType}
                      entities={entitiesByType.get(entityType) ?? []}
                      topics={topicsByType.get(entityType) ?? []}
                      skills={typeSkills}
                      running={allTypeSkills.some((s) => runningKeys.has(runKey(entityType, s.id)))}
                      onOpen={(tab) => openType(entityType, tab)}
                      onOpenEntity={onOpenEntity}
                    />
                  );
                })}
              </Box>
            </>
          )}

          {canEdit && availableTypes.length > 0 && (
            <FormControl size="small" sx={{ width: 240 }}>
              <Select
                displayEmpty
                value=""
                disabled={addType.isPending}
                onChange={(e) => {
                  if (e.target.value) addType.mutate(e.target.value);
                }}
                renderValue={() => (
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: 'text.secondary' }}>
                    <AddIcon fontSize="small" />
                    <Typography variant="body2">Add managed entity type</Typography>
                  </Stack>
                )}
              >
                {availableTypes.map((t) => (
                  <MenuItem key={t} value={t}>
                    {formatEntityTypeLabel(t)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Collapse>
      </Box>
    </GlassPanel>
  );
}
